import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getColors, fonts } from '../theme';
import { 
  Zap,
  TrendingUp, 
  Clock, 
  Target, 
  CheckCircle2, 
  Calendar, 
  Sparkles,
  ArrowRight,
  Shield,
  Smartphone,
  Moon,
  Flame,
  Rocket,
  Quote,
  ClipboardList,
  CalendarDays,
  Trophy
} from 'lucide-react';
import Logo from './Logo';

const HustleLandingPage = ({ isDark = true, onGetStarted }) => {
  const colors = getColors(isDark);
  const [isVisible, setIsVisible] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(0);
  const [isQuoteFading, setIsQuoteFading] = useState(false);

  const quotes = [
    {
      text: "You're not going to get to the next level by thinking about it. You get there by doing.",
      author: "Gary Vaynerchuk"
    },
    {
      text: "Discipline equals freedom.",
      author: "Jocko Willink"
    },
    {
      text: "Play long-term games with long-term people.",
      author: "Naval Ravikant"
    },
    {
      text: "The obstacle is the way.",
      author: "Ryan Holiday"
    }
  ];

  useEffect(() => {
    document.title = 'mind. | Execute. Track. Win.';
    
    return () => {
      document.title = 'mind.';
    };
  }, []);

  useEffect(() => {
    setIsVisible(true);
    
    const interval = setInterval(() => {
      setIsQuoteFading(true);
      setTimeout(() => {
        setCurrentQuote(prev => (prev + 1) % quotes.length);
        setIsQuoteFading(false);
      }, 500);
    }, 8000);
    
    return () => clearInterval(interval);
  }, []);

  const benefits = [
    {
      icon: CheckCircle2,
      title: "One habit. Zero excuses.",
      description: "5am wake-up. Cold shower. Daily workout. Pick one and stack.",
      color: colors.accent
    },
    {
      icon: Target,
      title: "Track the reps",
      description: "Streaks don't lie. Watch your consistency compound.",
      color: colors.accent
    },
    {
      icon: TrendingUp,
      title: "Ship faster",
      description: "Less planning, more executing. Log it and move on.",
      color: colors.accent
    }
  ];

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{ backgroundColor: colors.bg }}
    >

      {/* Navigation */}
      <nav className="px-6 max-w-5xl mx-auto relative z-10">
        <div className="flex items-end justify-between" style={{ borderBottom: `1px solid ${colors.textPrimary}` }}>
          <span className="text-[22px] leading-none pt-5 pb-3.5" style={{ fontFamily: fonts.serif, color: colors.textPrimary }}>mind.</span>
          <button onClick={onGetStarted} className="text-sm pb-3.5 hover:underline underline-offset-4" style={{ color: colors.textSecondary }}>Sign in</button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="px-6 pt-8 pb-16 md:pt-16 md:pb-24 max-w-4xl mx-auto relative z-10">
        <div 
          className={`text-center transition-all duration-300 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Tagline */}
          <div 
            className="inline-flex items-center gap-2 font-mono text-xs mb-8"
            style={{ color: colors.textMuted }}
          >
            <Zap size={16} />
            <span>Outwork yesterday</span>
          </div>

          {/* Quote */}
          <div className="mb-12 min-h-[180px] md:min-h-[200px] flex items-center justify-center relative">
            <blockquote 
              className="text-2xl md:text-3xl lg:text-4xl leading-relaxed transition-opacity duration-200 ease-in-out font-semibold tracking-[-0.035em]"
              style={{ 
                color: colors.textPrimary,
                opacity: isQuoteFading ? 0 : 1
              }}
            >
              "{quotes[currentQuote].text}"
              <footer 
                className="text-base md:text-lg mt-4 font-normal transition-opacity duration-200"
                style={{ color: colors.textMuted, opacity: isQuoteFading ? 0 : 1 }}
              >
                - {quotes[currentQuote].author}
              </footer>
            </blockquote>
          </div>

          {/* The pitch */}
          <p 
            className={`text-lg md:text-xl mb-12 max-w-2xl mx-auto leading-relaxed transition-all duration-300 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{ color: colors.textSecondary }}
          >
            You don't need another productivity system. You need to track the one thing 
            that moves the needle. 
            <span style={{ color: colors.accent }}> mind.</span> keeps you honest without 
            getting in your way.
          </p>

          {/* CTA */}
          <div 
            className={`transition-all duration-300 delay-400 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          >
            <button
              onClick={onGetStarted}
              className="group inline-flex items-center gap-3 px-8 py-4 rounded-lg text-base transition-all duration-150 "
              style={{ 
                backgroundColor: colors.accent,
                color: colors.onAccent
              }}
            >
              <span>Start now</span>
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <p 
              className="mt-4 text-sm"
              style={{ color: colors.textMuted }}
            >
              Free. No credit card. No friction.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="px-6 max-w-4xl mx-auto relative z-10">
        <div 
          className="h-px w-full"
          style={{ 
            backgroundColor: colors.border 
          }}
        />
      </div>

      {/* Benefits */}
      <section className="px-6 py-16 md:py-24 max-w-4xl mx-auto relative z-10">
        <h2 
          className="text-center text-xl md:text-2xl mb-12 font-semibold tracking-[-0.035em]"
          style={{ color: colors.textPrimary }}
        >
          Less noise. More reps.
        </h2>

        <div className="grid md:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="text-left pt-4 pb-2 transition-all duration-200"
              style={{ 
                borderTop: `1px solid ${colors.textPrimary}`,
                transitionDelay: `${index * 100}ms`
              }}
            >
              <div 
                className="inline-flex items-center justify-center w-10 h-10 mb-4"
                style={{ border: `1px solid ${colors.border}` }}
              >
                <benefit.icon size={22} strokeWidth={1.5} style={{ color: colors.textSecondary }} />
              </div>
              <h3 
                className="text-lg mb-2 font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                {benefit.title}
              </h3>
              <p 
                className="text-sm leading-relaxed"
                style={{ color: colors.textMuted }}
              >
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Concrete Examples */}
      <section className="px-6 py-16 max-w-4xl mx-auto relative z-10">
        <h2 
          className="text-center text-xl md:text-2xl mb-4 font-semibold tracking-[-0.035em]"
          style={{ color: colors.textPrimary }}
        >
          What high performers track
        </h2>
        <p 
          className="text-center text-sm mb-12"
          style={{ color: colors.textMuted }}
        >
          Real examples. Pick what compounds.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Habits */}
          <div 
            className="pt-4 pb-2"
            style={{ borderTop: `1px solid ${colors.textPrimary}`,
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 size={18} style={{ color: colors.accent }} />
              <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>Habits</span>
            </div>
            <div className="space-y-3">
              {[
                { habit: "Wake up at 5am", streak: 47 },
                { habit: "30 min deep work before email", streak: 23 },
                { habit: "Read 30 pages", streak: 89 },
                { habit: "Ship something every day", streak: 14 },
              ].map((item, i) => (
                <div 
                  key={i}
                  className="flex items-center justify-between p-3 rounded-md"
                  style={{ backgroundColor: colors.bg }}
                >
                  <span className="text-sm" style={{ color: colors.textSecondary }}>{item.habit}</span>
                  <div 
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                    style={{ 
                      backgroundColor: `${colors.warning}1a`,
                      color: colors.warning
                    }}
                  >
                    <Flame size={12} />
                    <span>{item.streak}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tasks */}
          <div 
            className="pt-4 pb-2"
            style={{ borderTop: `1px solid ${colors.textPrimary}`,
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList size={18} style={{ color: colors.textSecondary }} />
              <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>Tasks</span>
            </div>
            <div className="space-y-3">
              {[
                { task: "Finish pitch deck v3", priority: "Today" },
                { task: "Follow up with 10 leads", priority: "Today" },
                { task: "Record podcast episode", priority: "This week" },
                { task: "Review weekly metrics", priority: "Friday" },
              ].map((item, i) => (
                <div 
                  key={i}
                  className="flex items-center justify-between p-3 rounded-md"
                  style={{ backgroundColor: colors.bg }}
                >
                  <span className="text-sm" style={{ color: colors.textSecondary }}>{item.task}</span>
                  <span 
                    className="text-xs px-2 py-1 rounded-full"
                    style={{ 
                      backgroundColor: `${colors.accent}15`,
                      color: colors.accent
                    }}
                  >
                    {item.priority}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Goals */}
          <div 
            className="pt-4 pb-2"
            style={{ borderTop: `1px solid ${colors.textPrimary}`,
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Target size={18} style={{ color: colors.textSecondary }} />
              <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>Goals</span>
            </div>
            <div className="space-y-3">
              {[
                { goal: "100 cold calls this month", progress: 67, total: 100 },
                { goal: "Launch MVP by March", progress: 3, total: 5, unit: "sprints" },
                { goal: "Read 52 books this year", progress: 8, total: 52 },
              ].map((item, i) => (
                <div 
                  key={i}
                  className="p-3 rounded-md"
                  style={{ backgroundColor: colors.bg }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm" style={{ color: colors.textSecondary }}>{item.goal}</span>
                    <span className="text-xs" style={{ color: colors.textSecondary }}>{item.progress}/{item.total}</span>
                  </div>
                  <div 
                    className="h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: `${colors.border}50` }}
                  >
                    <div 
                      className="h-full rounded-full"
                      style={{ 
                        width: `${(item.progress / item.total) * 100}%`,
                        backgroundColor: colors.textSecondary
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Events & Quotes */}
          <div 
            className="pt-4 pb-2"
            style={{ borderTop: `1px solid ${colors.textPrimary}`,
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays size={18} style={{ color: colors.textSecondary }} />
              <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>Events & Quotes</span>
            </div>
            <div className="space-y-3">
              <div 
                className="flex items-center gap-3 p-3 rounded-md"
                style={{ backgroundColor: colors.bg }}
              >
                <Rocket size={16} style={{ color: colors.textSecondary }} />
                <div>
                  <span className="text-sm block" style={{ color: colors.textSecondary }}>Investor pitch</span>
                  <span className="text-xs" style={{ color: colors.textMuted }}>Tomorrow, 2:00 PM</span>
                </div>
              </div>
              <div 
                className="flex items-center gap-3 p-3 rounded-md"
                style={{ backgroundColor: colors.bg }}
              >
                <Trophy size={16} style={{ color: colors.accent }} />
                <div>
                  <span className="text-sm block" style={{ color: colors.textSecondary }}>Mastermind call</span>
                  <span className="text-xs" style={{ color: colors.textMuted }}>Thursday, 7:00 AM</span>
                </div>
              </div>
              {/* Daily Quote */}
              <div 
                className="p-3 rounded-md border-l-2"
                style={{ 
                  backgroundColor: colors.bg,
                  borderLeftColor: colors.accent
                }}
              >
                <Quote size={14} style={{ color: colors.textSecondary }} className="mb-1" />
                <p className="text-xs italic leading-relaxed" style={{ color: colors.textMuted }}>
                  "Hard choices, easy life. Easy choices, hard life."
                </p>
                <span className="text-xs" style={{ color: colors.textMuted }}>- Jerzy Gregorek</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 py-16 max-w-3xl mx-auto relative z-10">
        <div 
          className="pt-4 pb-2"
          style={{ borderTop: `1px solid ${colors.textPrimary}`,
            
          }}
        >
          <Sparkles size={28} style={{ color: colors.textSecondary }} className="mx-auto mb-6" />
          
          <h3 
            className="text-xl md:text-2xl mb-6 font-semibold tracking-[-0.035em]"
            style={{ color: colors.textPrimary }}
          >
            Built for speed. Not complexity.
          </h3>

          <div className="space-y-4 text-left max-w-md mx-auto mb-8">
            {[
              "Track up to 5 habits (free forever)",
              "Watch streaks compound over time",
              "Set goals with real deadlines",
              "No gamification BS, just data",
              "Works on every device",
              "Upgrade for unlimited. $5/mo. Cancel anytime."
            ].map((item, i) => (
              <div 
                key={i}
                className="flex items-center gap-3"
              >
                <div 
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${colors.accent}20` }}
                >
                  <CheckCircle2 size={12} style={{ color: colors.accent }} />
                </div>
                <span className="text-sm" style={{ color: colors.textSecondary }}>
                  {item}
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={onGetStarted}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm transition-all duration-150 "
            style={{ 
              backgroundColor: 'transparent',
              color: colors.accent,
              border: `1px solid ${colors.accent}50`
            }}
          >
            Get started free
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* Trust signals */}
      <section className="px-6 py-12 max-w-4xl mx-auto relative z-10">
        <div className="flex flex-wrap justify-center gap-8 text-center">
          {[
            { icon: Shield, text: "Your data stays private" },
            { icon: Smartphone, text: "Works everywhere" },
            { icon: Moon, text: "Dark mode included" }
          ].map((item, i) => (
            <div 
              key={i}
              className="flex items-center gap-2"
              style={{ color: colors.textMuted }}
            >
              <item.icon size={16} />
              <span className="text-sm">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 py-16 md:py-24 max-w-3xl mx-auto text-center relative z-10">
        <p 
          className="text-lg md:text-xl mb-8 leading-relaxed font-semibold tracking-tight"
          style={{ color: colors.textSecondary }}
        >
          Stop planning. Start executing.
          <span className="hidden md:inline"><br /></span>
          <span className="inline md:hidden"> </span>
          The compound effect is real.
        </p>
        
        <button
          onClick={onGetStarted}
          className="group inline-flex items-center gap-3 px-8 py-4 rounded-lg text-base transition-all duration-150 "
          style={{ 
            backgroundColor: colors.accent,
            color: colors.onAccent
          }}
        >
          <span>Start now</span>
          <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
        </button>
      </section>

      {/* Footer */}
      <footer className="px-6 py-8 border-t relative z-10" style={{ borderColor: colors.border + '30' }}>
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <Logo size="sm" isDark={isDark} />
          
          <div className="flex gap-6">
            <Link 
              to="/privacy" 
              className="text-sm hover:opacity-70 transition-opacity"
              style={{ color: colors.textMuted }}
            >
              Privacy
            </Link>
            <Link 
              to="/terms" 
              className="text-sm hover:opacity-70 transition-opacity"
              style={{ color: colors.textMuted }}
            >
              Terms
            </Link>
            <Link 
              to="/about" 
              className="text-sm hover:opacity-70 transition-opacity"
              style={{ color: colors.textMuted }}
            >
              About
            </Link>
          </div>

          <p className="text-xs" style={{ color: colors.textMuted }}>
            © 2026 mind.
          </p>
        </div>
      </footer>

    </div>
  );
};

export default HustleLandingPage;
